import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Button, Table } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import baseUrl from "../../../helpers/baseUrl";

const ViewCoupon = () => {
  document.title = "Estarch | Coupons";

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [formData, setFormData] = useState({ startDate: "", endDate: "", isActive: false });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/api/coupons`);
      setCoupons(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    }
  };

  const toggleCouponStatus = async (couponId, currentStatus) => {
    try {
      await axios.patch(`${baseUrl}/api/coupons/${couponId}/toggle`, { isActive: !currentStatus });
      setCoupons(prev =>
        prev.map(coupon => (coupon._id === couponId ? { ...coupon, isActive: !currentStatus } : coupon))
      );
    } catch (error) {
      console.error("Error toggling coupon status:", error);
    }
  };

  const openViewModal = (coupon) => {
    setSelectedCoupon(coupon);
    setViewModalOpen(true);
  };

  const openEditModal = (coupon) => {
    setSelectedCoupon(coupon);
    setFormData({
      startDate: new Date(coupon.startDate).toISOString().split("T")[0],
      endDate: new Date(coupon.endDate).toISOString().split("T")[0],
      isActive: coupon.isActive,
    });
    setEditModalOpen(true);
  };

  const handleUpdateCoupon = async () => {
    try {
      const updatedCoupon = { ...selectedCoupon, ...formData };
      await axios.put(`${baseUrl}/api/coupons/update/${selectedCoupon._id}`, updatedCoupon);
      setCoupons(prev =>
        prev.map(coupon => (coupon._id === selectedCoupon._id ? updatedCoupon : coupon))
      );
      setEditModalOpen(false);
    } catch (error) {
      console.error("Error updating coupon:", error);
    }
  };

  return (
    <div className="page-content bg-white">
      <Container fluid>
        <Breadcrumbs title="Estarch" breadcrumbItem="Coupons" />
        <div className="shadow-lg p-4 bg-gray-50 rounded-lg">
          <p className="w-full bg-gray-600 text-white p-2 font-bold text-2xl text-center rounded">All Coupons</p>
          {loading ? (
            <p className="text-center text-gray-500 mt-4">Loading coupons...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table responsive striped className="mt-3">
                <thead className="bg-gray-200">
                  <tr>
                    <th>#</th>
                    <th>Coupon Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Total Sold</th>
                    <th>Orders Used</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon, index) => (
                    <tr key={coupon._id}>
                      <td>{index + 1}</td>
                      <td>{coupon.name}</td>
                      <td>{new Date(coupon.startDate).toLocaleDateString()}</td>
                      <td>{new Date(coupon.endDate).toLocaleDateString()}</td>
                      <td>${coupon.totalSold}</td>
                      <td>{coupon.orderCount}</td>
                      <td>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={coupon.isActive}
                          />
                          <span className="slider round"></span>
                        </label>
                      </td>
                      <td>
                        <Button color="primary" size="sm" onClick={() => openViewModal(coupon)}>View</Button>{" "}
                        <Button color="warning" size="sm" onClick={() => openEditModal(coupon)}>Edit</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </Container>

      {/* View Modal */}
      {viewModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">Coupon Discount Rules</h2>
              <button onClick={() => setViewModalOpen(false)} className="text-gray-600 hover:text-gray-900">&times;</button>
            </div>
            <div className="p-4">
              {selectedCoupon?.discountRules?.length > 0 ? (
                selectedCoupon.discountRules.map((rule, index) => (
                  <div key={index} className="p-3 border rounded mb-2 bg-gray-100">
                    <p><strong>Min Spend:</strong> ${rule.minSpend}</p>
                    <p><strong>Discount:</strong> {rule.discountType === "fixed" ? `$${rule.discountAmount}` : `${rule.discountAmount}%`}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No discount rules available</p>
              )}
            </div>
            <div className="p-4 border-t flex justify-end">
              <button onClick={() => setViewModalOpen(false)} className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 p-6">
            <h2 className="text-lg font-semibold mb-4">Edit Coupon</h2>
            <label className="block mb-2">Start Date</label>
            <input type="date" className="w-full border p-2 rounded mb-4"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
            <label className="block mb-2">End Date</label>
            <input type="date" className="w-full border p-2 rounded mb-4"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            />
            <div className="flex items-center mb-4">
              <input type="checkbox" checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span className="ml-2">Active</span>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setEditModalOpen(false)} className="bg-gray-500 text-white px-4 py-2 rounded mr-2 hover:bg-gray-600">
                Cancel
              </button>
              <button onClick={handleUpdateCoupon} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCoupon;
